import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
} from "@surya-digital/leo-ts-types";
import { SurveyName, SurveyNameDTO } from "../types/surveyName";
import { UserName, UserNameDTO } from "../types/userName";
import { SurveyStatus } from "../types/surveyStatus";
import { SurveyLinkBreakdown, SurveyLinkBreakdownDTO } from "./surveyLinkBreakdown";
import { SurveySubmissionData, SurveySubmissionDataDTO } from "./surveySubmissionData";

export interface SurveyDetailsDTO {
  id: string;
  name: SurveyNameDTO;
  creator: UserNameDTO;
  status: SurveyStatus.SurveyStatusDTO;
  linkBreakdown: SurveyLinkBreakdownDTO;
  submissionData: SurveySubmissionDataDTO[];
}

export class SurveyDetails {
  readonly isSurveyDetailsSurveyDetails: boolean = true;

  readonly id: LeoUUID;
  readonly name: SurveyName;
  readonly creator: UserName;
  readonly status: SurveyStatus.SurveyStatus;
  readonly linkBreakdown: SurveyLinkBreakdown;
  readonly submissionData: SurveySubmissionData[];

  static readonly constraints = {
    "submissionData": {
      "maxSize": 45,
    }
  }

  constructor(
  id: LeoUUID, 
  name: SurveyName, 
  creator: UserName, 
  status: SurveyStatus.SurveyStatus, 
  linkBreakdown: SurveyLinkBreakdown, 
  submissionData: SurveySubmissionData[]
  ) {
    this.id = id;
    this.name = name;
    this.creator = creator;
    this.status = status;
    this.linkBreakdown = linkBreakdown;
    SurveyDetails.validateSubmissionData(submissionData);
    this.submissionData = submissionData;
  }

  static fromDTO(dto: SurveyDetailsDTO): SurveyDetails {
    const id = getLeoUuid(dto, "id", InvalidSurveyDetailsIdError);
    const name = SurveyName.fromDTO(getObject(dto, "name", InvalidSurveyDetailsNameError));
    const creator = UserName.fromDTO(getObject(dto, "creator", InvalidSurveyDetailsCreatorError));
    const status = SurveyStatus.fromDTO(getObject(dto, "status", InvalidSurveyDetailsStatusError));
    const linkBreakdown = SurveyLinkBreakdown.fromDTO(getObject(dto, "linkBreakdown", InvalidSurveyDetailsLinkBreakdownError));
    const submissionData = getList<SurveySubmissionDataDTO, SurveySubmissionData>(
      dto,
      "submissionData",
      SurveySubmissionData.fromDTO,
      InvalidSurveyDetailsSubmissionDataError
    );
    return new SurveyDetails(
      id,
      name,
      creator,
      status,
      linkBreakdown,
      submissionData
    );
  }

  toDTO(): SurveyDetailsDTO {
    const dto: SurveyDetailsDTO = {
        id: this.id.toDTO(),
        name: this.name.toDTO(),
        creator: this.creator.toDTO(),
        status: SurveyStatus.toDTO(this.status),
        linkBreakdown: this.linkBreakdown.toDTO(),
        submissionData: this.submissionData.map((e: SurveySubmissionData) => { return e.toDTO() })
    };
    return dto;
  }
  private static validateSubmissionData = (submissionData: SurveySubmissionData[]): void => {
    if(submissionData.length > 45) {
      throw new InvalidSurveyDetailsSubmissionDataMaxSizeError(
        45,
        `Attribute submissionData is too long. Size is ${submissionData.length}. Max size is 45.`
      );
    }
  }
  
}

SurveyDetails.prototype.toString = function toString(): string {
  return `SurveyDetails(id='${this.id}',name='${this.name}',creator='${this.creator}',status='${this.status}',linkBreakdown='${this.linkBreakdown}',submissionData='${this.submissionData}')`;
};

export class InvalidSurveyDetailsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsError";
  }
}
export class InvalidSurveyDetailsIdError extends InvalidSurveyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsIdError";
  }
}

export class InvalidSurveyDetailsNameError extends InvalidSurveyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsNameError";
  }
}

export class InvalidSurveyDetailsCreatorError extends InvalidSurveyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsCreatorError";
  }
}

export class InvalidSurveyDetailsStatusError extends InvalidSurveyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsStatusError";
  }
}

export class InvalidSurveyDetailsLinkBreakdownError extends InvalidSurveyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsLinkBreakdownError";
  }
}

export class InvalidSurveyDetailsSubmissionDataError extends InvalidSurveyDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsSubmissionDataError";
  }
}

export class InvalidSurveyDetailsSubmissionDataMaxSizeError extends InvalidSurveyDetailsSubmissionDataError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyDetailsSubmissionDataMaxSizeError";
  }
}
